<template>
  <ReassuranceItem>
    <template #icon>
      <IconTruckExpress />
    </template>
    <template #content>
      <ClientOnly>
        <div>
          <div class="body-2-bold">
            <FormattedMessage
              v-if="shouldDisplayFreeShippingWithDates"
              :definition="translations.freeShipping"
            >
              <template #dates>
                {{
                  i18n(translations.freeShippingDates, {
                    earliest: freeEarliestArrivalDate,
                    latest: freeLatestArrivalDate,
                  })
                }}
              </template>
            </FormattedMessage>

            <span v-if="shouldDisplayFreeShippingWithoutDates">
              {{ i18n(translations.freeShippingWithoutDates) }}
            </span>
          </div>

          <div v-if="shouldDisplayExpressShipping" class="caption">
            {{
              i18n(translations.expressShipping, {
                amount: expressShippingPriceLabel,
                earliest: expressEarliestArrivalDate,
                latest: expressLatestArrivalDate,
              })
            }}
          </div>
        </div>

        <template #fallback>
          <div>
            <div class="body-2-bold">
              {{ i18n(translations.freeShippingWithoutDates) }}
            </div>
            <RevSkeleton height="16px" shape="rectangle" width="100%" />
          </div>
        </template>
      </ClientOnly>
    </template>
  </ReassuranceItem>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import { productAPI } from '@backmarket/http-api'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevSkeleton } from '@ds/components/Skeleton'
import { IconTruckExpress } from '@ds/icons/IconTruckExpress'

import ReassuranceItem from '../ReassuranceItem/ReassuranceItem.vue'

import translations from './ItemShipping.translations'

const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
}

const props = defineProps<{
  shipping: productAPI.GetBestOffersResponse[number]['reassurance']['shipping']
}>()

const i18n = useI18n()

const shouldDisplayFreeShippingWithoutDates = computed(() => {
  return isEmpty(props.shipping?.free)
})

const shouldDisplayFreeShippingWithDates = computed(() => {
  return !isEmpty(props.shipping?.free)
})

const shouldDisplayExpressShipping = computed(() => {
  if (props.shipping?.express) {
    return Number(props.shipping.express.price.amount) > 0
  }

  return false
})

const freeEarliestArrivalDate = computed(() => {
  return props.shipping?.free
    ? i18n.date(new Date(props.shipping.free.earliestArrivalDate), DATE_FORMAT)
    : null
})

const freeLatestArrivalDate = computed(() => {
  return props.shipping?.free
    ? i18n.date(new Date(props.shipping.free.latestArrivalDate), DATE_FORMAT)
    : null
})

const expressEarliestArrivalDate = computed(() => {
  return props.shipping?.express
    ? i18n.date(
        new Date(props.shipping.express.earliestArrivalDate),
        DATE_FORMAT,
      )
    : null
})

const expressLatestArrivalDate = computed(() => {
  return props.shipping?.express
    ? i18n.date(new Date(props.shipping.express.latestArrivalDate), DATE_FORMAT)
    : null
})

const expressShippingPriceLabel = computed(() => {
  return props.shipping?.express
    ? i18n.price(props.shipping.express.price)
    : null
})
</script>
